import axios from "axios";
import { supabase } from "./supabase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

// Endpoints where an SA/Admin's "View as user" impersonation should be
// forwarded to the backend as `?executive_id=<uid>` (for /leads, /lead-pool,
// /followups/queue) or `?user_id=<uid>` (for /reports/day-end, day-end
// variants). Everything else is left untouched so writes always attribute to
// the acting user.
const IMPERSONATE_EXEC_PATHS = [
  "/leads",
  "/lead-pool",
  "/followups/queue",
  "/dashboard/stats",
  "/dashboard/timeseries",
];
const IMPERSONATE_USER_PATHS = ["/reports/day-end"];

function _readImpersonateId() {
  try {
    const raw = sessionStorage.getItem("gb_impersonate_user");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id || null;
  } catch { return null; }
}

// Attach the current Supabase access token to every outgoing request. When the
// SA/Admin is "viewing as" another user, also forward the impersonation intent
// so backend reads mirror the target user's world.
api.interceptors.request.use(async (config) => {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (token) config.headers.Authorization = `Bearer ${token}`;
  } catch {
    /* ignore — request will go out unauthenticated and get a 401 back */
  }

  // Auto-inject impersonation params on read endpoints. We only touch GETs.
  const impersonateId = _readImpersonateId();
  if (impersonateId && (config.method || "get").toLowerCase() === "get") {
    const url = config.url || "";
    const bare = url.split("?")[0].replace(/\/$/, "");
    config.params = config.params || {};
    const matchExec = IMPERSONATE_EXEC_PATHS.some((p) => bare === p || bare.startsWith(p + "/"));
    const matchUser = IMPERSONATE_USER_PATHS.some((p) => bare === p || bare.startsWith(p + "/"));
    if (matchExec && !config.params.executive_id) config.params.executive_id = impersonateId;
    if (matchUser && !config.params.user_id) config.params.user_id = impersonateId;
    // Also send the header for observability (backend can log dual attribution).
    config.headers["X-Impersonate-User"] = impersonateId;
  }
  return config;
});

// Fire a global "leads changed" event whenever any write to /leads or
// /lead-pool succeeds — sidebar badges (New Leads, Lead Pool, Lost Leads,
// Starred) listen and re-fetch their independent counts instantly. No shared
// state or cached totals: each badge queries its own scoped endpoint.
function _isLeadMutationUrl(url = "") {
  const bare = url.split("?")[0];
  return ["/leads", "/lead-pool", "/transfer-requests", "/data-requests", "/lead-shares", "/dummy"].some((p) => bare.startsWith(p));
}

// 401 → session gone/expired. Sign the user out cleanly.
// 401 SESSION_REVOKED → user was signed out because either (a) their session
// was manually revoked or (b) a new sign-in on another device pushed this
// one past the concurrent-device limit. Toast the message on login.
// 403 ACCESS_RESTRICTED → Super Admin has toggled the Master switch off or the
// night blackout is active. Force-logout the active session immediately so
// the user can't linger inside the CRM once access is revoked.
api.interceptors.response.use(
  (r) => {
    try {
      const method = (r.config?.method || "get").toLowerCase();
      if (method !== "get" && _isLeadMutationUrl(r.config?.url || "")) {
        window.dispatchEvent(new CustomEvent("gb-leads-changed"));
      }
    } catch { /* ignore */ }
    return r;
  },
  async (err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.detail?.code;
    if (status === 401 || (status === 403 && code === "ACCESS_RESTRICTED")) {
      try { sessionStorage.removeItem("gb_just_signed_in"); } catch { /* ignore */ }
      if (status === 403 && code === "ACCESS_RESTRICTED") {
        try {
          const msg = err?.response?.data?.detail?.message || "CRM access has been restricted.";
          sessionStorage.setItem("gb_access_restricted_msg", msg);
        } catch { /* ignore */ }
      }
      if (status === 401 && code === "SESSION_REVOKED" && !window.__gbManualLogout) {
        try {
          const msg = err?.response?.data?.detail?.message || "You were signed out on this device.";
          sessionStorage.setItem("gb_session_revoked_msg", msg);
        } catch { /* ignore */ }
      }
      try { await supabase.auth.signOut(); } catch { /* ignore */ }
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);
